/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Text, View, DraggablePositionType } from '@components/atoms'
import { PdfDraggableView } from './PdfDraggableView'

import { SignPositionType, SignPositionTargetType } from '../PdfReader'
import { useAppSelector } from '@redux/index'
import { SignPositionOffsetType } from '@schemas'
import { blue, deepPurple, grey } from '@mui/material/colors'

interface PropsType {
	mode: 'none' | 'add' | 'update' | 'sign'
	onStop: (e: DraggablePositionType) => void
	onSubmit: () => any
	onClose: () => void
	showSignature?: boolean
	currentPage: number
	onSetting: () => void
	positionTarget: SignPositionTargetType
}

export const DraggableSignTextPosition = (props: PropsType) => {
	const { mode, onStop, onClose, currentPage, positionTarget } = props

	// redux state
	const selectedPosition: SignPositionOffsetType[] =
		mode === 'sign'
			? useAppSelector(state => state.contract.selectedContractPosition)
			: useAppSelector(state => state.salesContractMgmt.selectedContractPosition)
	const signPosition: SignPositionType = useAppSelector(
		state => state.salesContractMgmt.signPosition,
	)

	// state
	const [isSetting, setIsSetting] = React.useState<boolean>(false)

	// variables
	const positionIdx = selectedPosition.findIndex(
		aPosition => aPosition.id === positionTarget,
	)

	const positionOffset: SignPositionOffsetType = React.useMemo(() => {
		if (mode === 'sign' || mode === 'update') {
			const res =
				positionTarget === 'address' ||
				positionTarget === 'address2' ||
				positionTarget === 'address3'
					? { ...selectedPosition[positionIdx], x: selectedPosition[positionIdx].x - 150 }
					: positionTarget === 'check'
					? { ...selectedPosition[positionIdx], x: selectedPosition[positionIdx].x + 100 }
					: selectedPosition[positionIdx]
			return res
		}

		return {
			id: '',
			x: 0,
			y: 0,
			page: 0,
		}
	}, [mode, positionIdx, selectedPosition])

	const title =
		mode === 'sign'
			? selectedPosition[positionIdx].value
			: positionTarget === 'check'
			? '✔'
			: positionTarget === 'signedAt' ||
			  positionTarget === 'signedAt2' ||
			  positionTarget === 'signedAt3'
			? 'YYYY년 MM월 DD일'
			: signPosition[positionTarget].title
	const size: { width: number; height: number } = React.useMemo(() => {
		const res =
			positionTarget === 'signedAt' ||
			positionTarget === 'signedAt2' ||
			positionTarget === 'signedAt3'
				? { width: 200, height: 30 }
				: positionTarget === 'address' ||
				  positionTarget === 'address2' ||
				  positionTarget === 'address3'
				? { width: 350, height: 30 }
				: positionTarget === 'check'
				? { width: 100, height: 30 }
				: positionTarget === 'sign'
				? { width: 200, height: 69.6 }
				: { width: 200, height: 30 }

		if (mode === 'sign') {
			return {
				...res,
				width: res.width - 28,
			}
		}

		return res
	}, [mode, positionTarget])

	React.useEffect(() => {
		setIsSetting(true)
	}, [])

	React.useEffect(() => {
		if (mode === 'update') {
			setIsSetting(false)
		} else if (mode === 'add') {
			setIsSetting(true)
		} else if (mode === 'sign') {
			setIsSetting(false)
		}
	}, [mode])

	const onSetting = () => {
		setIsSetting(true)
		props.onSetting()
	}

	const onSubmit = () => {
		setIsSetting(false)
		props.onSubmit()
	}

	return (
		<PdfDraggableView
			mode={mode}
			onStop={onStop}
			onSubmit={onSubmit}
			onClose={onClose}
			showCloseButton={isSetting}
			showSubmitButton={isSetting}
			showSettingButton={mode === 'update' || mode === 'sign' ? false : !isSetting}
			disabled={!isSetting || mode === 'sign'}
			onSetting={onSetting}
			positionOffset={
				mode === 'update' || mode === 'sign'
					? {
							...positionOffset,
							x: positionOffset.x,
							y: positionOffset.y,
					  }
					: undefined
			}
			hide={
				mode === 'add' && !isSetting && currentPage !== signPosition[positionTarget].page
					? true
					: ['update', 'sign'].includes(mode) && currentPage !== positionOffset.page
					? true
					: false
			}
		>
			<View css={[style, { ...size }]}>
				<Text
					category="h6"
					className={`draggableSignPositionLabel ${
						mode === 'sign' ? 'signMode' : isSetting ? '' : 'disabled'
					}`}
					css={{ textAlign: positionTarget === 'check' ? 'center' : 'left' }}
				>
					{title}
				</Text>

				{isSetting && mode !== 'sign' && (
					<Text category="c2" className="draggableSignPositionIndicator">
						{"위치를 지정하고 'V'를 클릭하세요."}
					</Text>
				)}
			</View>
		</PdfDraggableView>
	)
}

const style = css({
	position: 'relative',
	width: '100%',
	paddingRight: '1.7rem',
	paddingLeft: '0.5rem',

	'.draggableSignPositionLabel': {
		height: '100%',
		width: '100%',
		margin: 0,
		paddingTop: '0.4rem',
		color: deepPurple['300'],

		'&.disabled': {
			color: grey[800],
		},

		'&.signMode': {
			color: blue['A400'],
		},
	},

	'.draggableSignPositionIndicator': {
		position: 'absolute',
		bottom: '-2rem',
		color: deepPurple['A400'],
	},
})
